import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  Calendar,
  Users,
  Settings,
  LogOut,
  Home,
  Menu,
  X,
  Shield,
  FileCheck,
  FileText,
  ChevronDown,
  Check,
  BookOpen,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { NotificationBell } from './NotificationBell'
import { BrandLogo } from './BrandLogo'
import { api } from '../lib/api'

interface DashboardLayoutProps {
  children: React.ReactNode
  title: string
  subtitle?: string
  actions?: React.ReactNode
}

// Manage pages live under /dashboard; the personal pages are /my-bookings and the
// insurance page. Manage pages need the selected org to be one the user manages.
const PERSONAL_PATHS = ['/my-bookings', '/dashboard/pli-certificates', '/dashboard/insurance']

export function DashboardLayout({ children, title, subtitle, actions }: DashboardLayoutProps) {
  const { user, currentOrg, memberships, setCurrentOrg } = useAuthStore()
  const location = useLocation()
  const queryClient = useQueryClient()

  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const managedOrgs = memberships.filter((m) => m.role === 'admin' || m.role === 'approver')

  // On manage pages, make sure the selected org is one the user actually manages.
  // (Booking in the marketplace can leave a guest org selected.)
  const isManagePath =
    location.pathname.startsWith('/dashboard') &&
    !PERSONAL_PATHS.some((p) => location.pathname.startsWith(p))
  const currentIsManaged = managedOrgs.some((m) => m.orgSlug === currentOrg?.orgSlug)

  useEffect(() => {
    if (isManagePath && !currentIsManaged && managedOrgs.length > 0) {
      setCurrentOrg(managedOrgs[0]!.orgSlug).then(() => {
        queryClient.clear()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagePath, currentIsManaged, managedOrgs.length])

  const handleLogout = async () => {
    await useAuthStore.getState().logout()
    window.location.href = '/'
  }

  if (!user) return null

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <div
        id="mobile-navigation"
        inert={!sidebarOpen}
        className={`fixed inset-y-0 left-0 w-[var(--rs-sidebar-w)] max-w-[85vw] bg-white border-r border-gray-200 flex flex-col z-50 transform transition-transform duration-200 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent onClose={() => setSidebarOpen(false)} onLogout={handleLogout} />
      </div>

      {/* Desktop sidebar - hidden on mobile */}
      <div className="hidden lg:flex fixed inset-y-0 left-0 w-[var(--rs-sidebar-w)] bg-white border-r border-gray-200 flex-col">
        <SidebarContent onLogout={handleLogout} />
      </div>

      {/* Main content */}
      <div className="min-w-0 lg:pl-[var(--rs-sidebar-w)]">
        {/* Header - sticky on mobile */}
        <header className="min-h-20 bg-white border-b border-gray-200 flex flex-wrap items-center justify-between gap-y-3 px-4 py-4 lg:px-8 sticky top-0 z-30 lg:static">
          {/* Mobile hamburger menu */}
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
            aria-controls="mobile-navigation"
            title="Open navigation"
            className="p-2 -ml-2 mr-2 text-gray-600 hover:text-gray-900 lg:hidden"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex-1 lg:flex-none min-w-0">
            <h1 className="text-xl font-bold text-gray-900 break-words">{title}</h1>
            {subtitle && <p className="text-sm text-gray-600 hidden lg:block">{subtitle}</p>}
          </div>
          <div className="flex max-w-full flex-wrap items-center gap-2 lg:gap-3">
            {actions}
            <NotificationBell />
          </div>
        </header>

        {/* Content */}
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  )
}

// The single sidebar, shared by the mobile drawer and the desktop column so the
// navigation can never drift between the two.
function SidebarContent({ onClose, onLogout }: { onClose?: () => void; onLogout: () => void }) {
  const { user, currentOrg, memberships, setCurrentOrg } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const managedOrgs = memberships.filter((m) => m.role === 'admin' || m.role === 'approver')

  // The org shown in the Manage section: the selected org when it's one the user
  // manages, otherwise their first managed org.
  const activeManaged =
    managedOrgs.find((m) => m.orgSlug === currentOrg?.orgSlug) || managedOrgs[0] || null
  const manageRole = activeManaged?.role

  const [orgPickerOpen, setOrgPickerOpen] = useState(false)

  // Badge: pending bookings + (for admins) pending access requests, shown on Bookings
  const [bookingsBadge, setBookingsBadge] = useState(0)
  useEffect(() => {
    if (!activeManaged || activeManaged.orgSlug !== currentOrg?.orgSlug) return
    let cancelled = false
    const load = async () => {
      try {
        const [{ count: pending }, requests] = await Promise.all([
          api.bookings.getPendingBookingsCount(),
          activeManaged.role === 'admin'
            ? api.bookings.getAccessRequestsCount()
            : Promise.resolve({ count: 0 }),
        ])
        if (!cancelled) setBookingsBadge(pending + requests.count)
      } catch {
        // Badge is decorative; ignore failures
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [activeManaged, currentOrg?.orgSlug, activeManaged?.role])

  const switchManagedOrg = async (orgSlug: string) => {
    setOrgPickerOpen(false)
    if (orgSlug === currentOrg?.orgSlug) return
    await setCurrentOrg(orgSlug)
    // Drop every cached query so nothing from the previous org lingers; the manage
    // pages remount (keyed by org) and reload their data. No full page reload.
    queryClient.clear()
    navigate({ to: '/dashboard' })
  }

  if (!user) return null

  const linkClass = (active: boolean) =>
    `flex items-center gap-3 px-4 py-3 mx-3 rounded-md transition-colors text-sm font-medium ${
      active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
    }`

  return (
    <>
      {/* Logo */}
      <div className="h-20 flex items-center px-6 border-b border-gray-200 shrink-0">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo />
        </Link>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close navigation"
            title="Close navigation"
            className="ml-auto p-2 -mr-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {/* Personal - everyone has these */}
        <div className="mb-6">
          <Link to="/my-bookings" className={linkClass(location.pathname === '/my-bookings')}>
            <Calendar className="w-4 h-4" />
            My bookings
          </Link>
          <Link
            to="/dashboard/pli-certificates"
            className={linkClass(location.pathname === '/dashboard/pli-certificates')}
          >
            <FileCheck className="w-4 h-4" />
            My insurance
          </Link>
          <div className="px-4 mt-3">
            <Link
              to="/marketplace"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-semibold"
            >
              <Building2 className="w-4 h-4" />
              Book a room
            </Link>
          </div>
        </div>

        {/* Manage - only for admins/approvers of at least one org */}
        {activeManaged && (
          <div className="mb-6 pt-4 border-t border-gray-100">
            <div className="px-4 mb-2">
              <div className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-2">
                <Home className="w-3.5 h-3.5" />
                Manage
              </div>
            </div>

            {/* Which organisation is being managed */}
            {managedOrgs.length > 1 ? (
              <div className="relative mx-2 mb-2">
                <button
                  onClick={() => setOrgPickerOpen(!orgPickerOpen)}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="flex-1 text-sm font-semibold text-gray-900 truncate">
                    {activeManaged.orgName || activeManaged.orgSlug}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${orgPickerOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {orgPickerOpen && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                    {managedOrgs.map((m) => (
                      <button
                        key={m.orgId}
                        onClick={() => switchManagedOrg(m.orgSlug)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                      >
                        <span className="flex-1 truncate font-medium text-gray-900">
                          {m.orgName || m.orgSlug}
                        </span>
                        {m.orgSlug === activeManaged.orgSlug && (
                          <Check className="w-4 h-4 text-primary-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mx-2 mb-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-sm font-semibold text-gray-900 truncate block">
                  {activeManaged.orgName || activeManaged.orgSlug}
                </span>
              </div>
            )}

            <Link to="/dashboard" className={linkClass(location.pathname === '/dashboard')}>
              <Calendar className="w-4 h-4" />
              Calendar
            </Link>
            <Link
              to="/dashboard/bookings"
              className={linkClass(
                location.pathname === '/dashboard/bookings' ||
                  location.pathname === '/dashboard/requests'
              )}
            >
              <Calendar className="w-4 h-4" />
              Bookings
              {bookingsBadge > 0 && (
                <span className="ml-auto px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  {bookingsBadge}
                </span>
              )}
            </Link>
            <Link
              to="/dashboard/rooms"
              className={linkClass(
                location.pathname.startsWith('/dashboard/rooms') ||
                  location.pathname.startsWith('/dashboard/buildings')
              )}
            >
              <Building2 className="w-4 h-4" />
              Rooms
            </Link>
            {manageRole === 'admin' && (
              <Link
                to="/dashboard/people"
                className={linkClass(location.pathname === '/dashboard/people')}
              >
                <Users className="w-4 h-4" />
                Guests
              </Link>
            )}
            <Link
              to="/dashboard/invoices"
              className={linkClass(location.pathname === '/dashboard/invoices')}
            >
              <FileText className="w-4 h-4" />
              Invoices
            </Link>
            {manageRole === 'admin' && (
              <Link
                to="/dashboard/settings"
                search={{ stripe: undefined }}
                className={linkClass(
                  location.pathname.startsWith('/dashboard/settings') ||
                    location.pathname === '/dashboard/audit'
                )}
              >
                <Settings className="w-4 h-4" />
                Settings
              </Link>
            )}
          </div>
        )}

        <a href="/help/" target="_blank" rel="noopener noreferrer" className={linkClass(false)}>
          <BookOpen className="w-4 h-4" />
          Admin guide
          <span className="sr-only"> (opens in a new tab)</span>
        </a>

        {/* Global Admin - only for global admins */}
        {user.isGlobalAdmin && (
          <div className="mb-6 pt-4 border-t border-gray-100">
            <div className="px-4 mb-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" />
                System
              </div>
            </div>
            <Link to="/admin" className={linkClass(location.pathname === '/admin')}>
              <Shield className="w-4 h-4" />
              Global Admin
            </Link>
          </div>
        )}
      </div>

      {/* User */}
      <div className="shrink-0 p-4 border-t border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
            <div className="text-xs text-gray-500 truncate">{user.email}</div>
          </div>
          <button
            onClick={onLogout}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  )
}
